import {
  Vector3,
  Matrix4,
  Mesh,
  InstancedMesh,
  MeshStandardMaterial,
  ConeGeometry,
  MeshBasicMaterial,
  Object3D,
  Quaternion,
  CylinderGeometry,
  Color,
  DoubleSide,
} from 'three'
import { useFrame, useThree } from '@react-three/fiber'
import { useEffect, useMemo } from 'react'
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils'
import { useControls } from 'leva'
import { MeshSurfaceSampler } from 'three/examples/jsm/math/MeshSurfaceSampler'
import { useSpring, config } from '@react-spring/web'

const resample = (
  instancedMesh: InstancedMesh,
  flowerIndex: number,
  dummy: any,
  sampler: any,
  origin: Vector3,
  type: string = 'default'
) => {
  let position = new Vector3()
  let normal = new Vector3()

  sampler.sample(position, normal)
  normal.add(position).add(origin)

  dummy.position.copy(position.add(origin))

  if (type === 'default') {
    dummy.lookAt(normal)
  } else {
    // The chestnut tree's flowers look at the top
    dummy.lookAt(new Vector3(normal.x, normal.y * 1.2, normal.z))
  }

  dummy.updateMatrix()

  instancedMesh.setMatrixAt(flowerIndex, dummy.matrix)
}

export const Flower = ({
  tree,
  petal,
  maxFlowersCount,
  color = new Color(0xffffff),
  emissive = new Color(0x000000),
  name,
  type = 'default',
  _bottomRadius = 0.17,
  _topRadius = 20,
  _flowersCount = 500,
  _petalsCount = 5,
  _posAlongStem = 1.25,
  _size = 0.2,
  _bloomingTime = 0,
  _coloringTime = 0,
  _fallingTime = 0,
  _animate = true,
}: {
  tree: Mesh
  petal: Mesh
  maxFlowersCount: number
  color?: Color
  emissive?: Color
  name?: string
  type?: string
  _bottomRadius?: number
  _topRadius?: number
  _flowersCount?: number
  _petalsCount?: number
  _posAlongStem?: number
  _size?: number
  _bloomingTime?: number
  _coloringTime?: number
  _fallingTime?: number
  _animate?: boolean
}) => {
  const stemDummy = useMemo(() => new Object3D(), [])
  const flowerDummy = useMemo(() => new Object3D(), [])
  const treePosition = tree.parent ? tree.parent.position : new Vector3(0, 0, 0)
  const { scene } = useThree()
  const maxPetalsCount = 10

  const bloomingSpring = useSpring({
    time: _bloomingTime,
    config: config.molasses,
    onChange: ({ value }) => {
      setControls({ bloomingTime: value.time })
    },
    immediate: !_animate,
    onRest: () => { },
  })

  const fallingSpring = useSpring({
    time: _fallingTime,
    config: { duration: 2000 },
    onChange: ({ value }) => {
      setControls({ fallingTime: value.time })
    },
    immediate: !_animate,
    onRest: () => { },
  })

  const coloringSpring = useSpring({
    time: _coloringTime,
    config: config.molasses,
    onChange: ({ value }) => {
      setControls({ coloringTime: value.time })
    },
    immediate: !_animate,
    onRest: () => { },
  })

  const [
    {
      flowersCount,
      petalsCount,
      bottomRadius,
      size,
      topRadius,
      posAlongStem,
      bloomingTime,
      coloringTime,
      fallingTime,
    },
    setControls,
  ] = useControls(name ? name : Math.random().toFixed(5), () => ({
    flowersCount: {
      value: _flowersCount,
      min: 0,
      max: maxFlowersCount,
      step: 1,
    },
    petalsCount: { value: _petalsCount, min: 0, max: maxPetalsCount, step: 1 },
    size: { value: _size, min: 0, max: 2, step: 0.01 },
    topRadius: { value: _topRadius, min: 0, max: 40, step: 0.01 },
    bottomRadius: { value: _bottomRadius, min: 0, max: 2, step: 0.01 },
    posAlongStem: { value: _posAlongStem, min: 0, max: 5, step: 0.01 },
    bloomingTime: { value: _bloomingTime, min: 0, max: 1, step: 0.01 },
    coloringTime: { value: _coloringTime, min: 0, max: 1, step: 0.01 },
    fallingTime: { value: _fallingTime, min: 0, max: 1, step: 0.01 },
  }))

  const fallSpeed = 70

  // Les données de chaque fleur
  const flowersData = useMemo(
    () =>
      new Array(maxFlowersCount).fill(0).map(() => ({
        speed: 1 + Math.random() * 0.75,
        rd: Math.random(),
      })),
    [maxFlowersCount]
  )

  // Les données de chaque pétale
  const petalsData = useMemo(
    () =>
      new Array(maxPetalsCount * maxFlowersCount).fill(0).map(() => ({
        rd: Math.random(),
      })),
    [maxFlowersCount, maxPetalsCount]
  )

  // The buffer geometry for the stem (just for debugging purposes
  // as it only shows the stem's direction)
  const stemInstancedMesh = useMemo(() => {
    const arrowLength = 1
    const cone = new ConeGeometry(0.15, 0.25, 5)
    cone.translate(0, arrowLength * 0.5, 0)
    const cylinder = new CylinderGeometry(0.05, 0.05, arrowLength, 5)
    const arrowGeometry = BufferGeometryUtils.mergeGeometries([cone, cylinder])
    const arrowMaterial = new MeshBasicMaterial({
      color: color ? color : 0xffffff,
    })
    arrowGeometry.translate(0, arrowLength * 0.5, 0)
    arrowGeometry.applyMatrix4(new Matrix4().makeRotationX(Math.PI / 2))
    return new InstancedMesh(arrowGeometry, arrowMaterial, maxFlowersCount)
  }, [maxFlowersCount])

  // The buffer geometry for the petals
  const petalInstancedMesh = useMemo(() => {
    const geometry = petal.geometry.clone()
    const material = (petal.material as MeshStandardMaterial).clone()

    // this is the base color, this color is multiplied with the vertex color
    // defined in color and applied to the petal each frame
    material.color = new Color(0xffffff)

    // We need to set the emissive color to the petal's color otherwise it could be too dark
    material.emissive = emissive
    material.side = DoubleSide

    return new InstancedMesh(
      geometry,
      material,
      maxPetalsCount * maxFlowersCount
    )
  }, [maxFlowersCount, petal, maxPetalsCount])

  const sampler = useMemo(
    () => new MeshSurfaceSampler(tree).setWeightAttribute('color').build(),
    [tree]
  )

  useEffect(() => {
    for (let i = 0; i < maxFlowersCount; i += 1) {
      resample(stemInstancedMesh, i, stemDummy, sampler, treePosition, type)
    }

    // Add the petals to the scene
    // scene.add(stemInstancedMesh)
    scene.add(petalInstancedMesh)
  }, [])

  useEffect(() => {
    stemInstancedMesh.count = flowersCount
    petalInstancedMesh.count = petalsCount * flowersCount
  }, [flowersCount, petalsCount])

  useFrame(() => {
    for (let flowerIndex = 0; flowerIndex < flowersCount; flowerIndex++) {
      /************/
      /* Blooming */
      /************/

      // Get the flowers's data (speed of growth)
      const s = flowersData[flowerIndex].speed
      const flowerRandomValue = flowersData[flowerIndex].rd

      // The flowers should not bloom all at the same time to give a more natural look
      const t = Math.max(Math.min(bloomingTime * 1.75 * s + (s - 1.75), 1), 0)

      // Animate the base and top petals arrangement around the center
      const _bottomRadius = bottomRadius * t
      const _topRadius = topRadius * t * t * t

      // Get the center of the flower and apply it's transformations to the dummy
      stemInstancedMesh.getMatrixAt(flowerIndex, stemDummy.matrix)

      // We need to keep a copy of the center's rotation
      const firstRotation = new Quaternion().setFromRotationMatrix(
        stemDummy.matrix
      )

      // Get the position of the stem (gives the position and orientation of the flower's petals)
      const position = new Vector3()
      position.setFromMatrixPosition(stemDummy.matrix)

      // Petals
      for (let i = 0; i < petalsCount; i++) {
        // Get the petal index
        const petalIndex = flowerIndex * petalsCount + i

        // get the petal's size for the chestnut tree, the leaves are small
        // then bigger then small again
        let sValue
        if (i <= 3) {
          sValue = i + 1
        } else {
          sValue = 7 - i
        }
        const petalSize = type === 'default' ? size * sValue * 0.25 : size

        // Calculate the angle around the circle for this instance
        // Add a random value to the angle to give a more natural look
        const angle =
          (i / petalsCount) * Math.PI * 2 + flowerRandomValue * Math.PI * 2

        // We create a vector in the X plane, then rotate it around the Z (remember that we rotated the flower's center
        // so the Z axis is now the up direction) to get a vector that points to the right direction)
        // axis according to the angle
        const circleVectorBottom = new Vector3(
          _bottomRadius,
          0,
          0
        ).applyAxisAngle(new Vector3(0, 0, 1), angle)
        const circleVectorTop = new Vector3(_topRadius, 0, 0).applyAxisAngle(
          new Vector3(0, 0, 1),
          angle
        )

        // Now we rotate this vector according to the first instance's rotation,
        // to align it with the surface's orientation
        circleVectorBottom.applyQuaternion(firstRotation)
        circleVectorTop.applyQuaternion(firstRotation)

        // Then we add the position to get the final position of the petal
        const petalPosition = position.clone().add(circleVectorBottom)
        const target = position.clone().add(circleVectorTop)

        // Perhaps we can position the flower somewhere along the arrow
        // but not at its base
        if (type === 'default') {
          petalPosition.add(
            new Vector3(0, 0, posAlongStem * t).applyQuaternion(firstRotation)
          )
        } else {
          // The chestnut tree's flowers are more like a cone
          petalPosition.add(
            new Vector3(0, 0, posAlongStem * t + i * 0.1).applyQuaternion(
              firstRotation
            )
          )
        }

        // The petal's up direction should be the same as the first instance's
        const petalUp = new Vector3(0, 1, 0).applyQuaternion(firstRotation)

        // We can now set the petal's position
        flowerDummy.position.copy(petalPosition)
        flowerDummy.up.copy(petalUp)
        flowerDummy.quaternion.copy(firstRotation)

        flowerDummy.scale.set(
          t * t * petalSize,
          petalSize,
          (1 - Math.pow(1 - t, 3)) * petalSize
        )

        // The petal's target should be at the top of the flower
        target.add(new Vector3(0, 0, 10).applyQuaternion(firstRotation))

        // dummy.rotateZ(Math.PI)
        flowerDummy.lookAt(target)

        // correct the angle
        flowerDummy.rotateZ(angle + Math.PI / 2)

        // Update matrix after all the position and rotation changes
        // and set its matrix to the petal's instance
        flowerDummy.updateMatrix()
        petalInstancedMesh.setMatrixAt(petalIndex, flowerDummy.matrix)

        /*******************/
        /* Falling petals */
        /*******************/

        // Get the petal random value (used to give a more natural look to the petals)
        const rd = petalsData[petalIndex].rd
        const staggerTime = rd * 0.25
        const groundHitTime = petalPosition.y / fallSpeed

        let fallTime = Math.max(Math.min(fallingTime - staggerTime, 1), 0)

        if (fallTime > groundHitTime) {
          fallTime = groundHitTime
        }

        // The fall start shoudl vary according to the petal's random value (rd)
        // to give a more natural look
        // While falling, the petal should rotate to give a more natural look (cos and sin)
        const newPetalPosition = new Vector3(
          (Math.cos(fallTime * 12 * rd) - 1) * 5,
          fallTime * -fallSpeed,
          Math.sin(fallTime * 12 * rd) * 5
        )

        // console.log(
        //   'initial petal pos:', petalPosition.y,
        //   '\nnew petal pos:', newPetalPosition.y,
        //   '\nstagger time:', staggerTime,
        //   '\ncalculated pos:', fallTime * -fallSpeed,
        //   '\nfall time:', fallTime,
        //   '\nground hit time', groundHitTime,
        //   '\n')

        // We add the petal's fall vector to the initial petal's position
        petalPosition.add(newPetalPosition)
        flowerDummy.position.copy(petalPosition)

        // The petal should rotate while falling
        const rotationFactor = fallTime * 15 * rd
        flowerDummy.rotateX(rotationFactor)
        flowerDummy.rotateY(rotationFactor)
        flowerDummy.rotateZ(rotationFactor)

        // The closest to the ground the petal is, the more it should be rotated towards the ground
        // flowerDummy.lookAt(new Vector3(rd*900, 0, 90))

        // Update matrix after all the position and rotation changes
        flowerDummy.updateMatrix()
        petalInstancedMesh.setMatrixAt(petalIndex, flowerDummy.matrix)

        /**********/
        /* Colors */
        /**********/
        petalInstancedMesh.setColorAt(
          petalIndex,
          new Color(
            Math.min(color.r + coloringTime * 0.85, 1),
            Math.max(color.g - coloringTime * 0.15, 0),
            Math.max(color.b - coloringTime * 0.85, 0)
          )
        )
      }
    }

    petalInstancedMesh.instanceMatrix.needsUpdate = true
    if (petalInstancedMesh.instanceColor) {
      petalInstancedMesh.instanceColor.needsUpdate = true
    }
  })

  return null
}
